import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import type { DayEntry, CompanySettings, AppState, VacationCarryOver, SettingsSegment, WageType } from '../types';
import { DEFAULT_SETTINGS } from '../types';
import { getHolidaysForState } from '../data/germanHolidays';
import { mergeCompanyVacationIntoEntries, COMPANY_VACATION_DEFAULT_NOTE } from '../utils/companyVacation';

const STORAGE_KEYS = {
  SETTINGS: 'urlaubsrechner_settings',
  ENTRIES_PREFIX: 'urlaubsrechner_entries_',
  CARRY_OVER_PREFIX: 'urlaubsrechner_carryover_',
  SETTINGS_SEGMENTS_PREFIX: 'urlaubsrechner_settings_segments_',
  LAST_YEAR: 'urlaubsrechner_last_year',
};

type AppAction =
  | { type: 'SET_YEAR'; year: number }
  | { type: 'SET_SETTINGS'; settings: CompanySettings }
  | { type: 'UPDATE_ENTRY'; entry: DayEntry }
  | { type: 'SET_ENTRIES'; entries: Record<string, DayEntry> }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'RESET_YEAR_DATA' }
  | { type: 'IMPORT_DATA'; entries: Record<string, DayEntry>; settings: CompanySettings; year: number }
  | { type: 'SET_CARRY_OVER'; carryOver: VacationCarryOver | null }
  | { type: 'SET_SETTINGS_SEGMENTS'; settingsSegments: SettingsSegment[] }
  | { type: 'SET_USER_WAGE_TYPE'; wageType: WageType | null };

interface AppContextType {
  state: AppState;
  setYear: (year: number) => void;
  updateSettings: (settings: CompanySettings) => void;
  updateEntry: (entry: DayEntry) => void;
  resetYearData: () => void;
  importData: (entries: Record<string, DayEntry>, settings: CompanySettings, year?: number) => void;
  initializeHolidays: () => void;
  applyCompanyVacation: (dates: Set<string>, wageType: WageType | null) => void;
  carryOverVacation: (days: number) => void;
  clearCarryOver: () => void;
  setCarryOver: (carryOver: VacationCarryOver | null) => void;
  getCarryOverFromPreviousYear: () => number;
  getPreviousYearEntries: () => Record<string, DayEntry>;
  setSettingsSegments: (segments: SettingsSegment[]) => void;
  setUserWageType: (wageType: WageType | null) => void;
}

const AppContext = createContext<AppContextType | null>(null);

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_YEAR':
      return { ...state, year: action.year };
    case 'SET_SETTINGS':
      return { ...state, settings: action.settings };
    case 'UPDATE_ENTRY':
      return {
        ...state,
        entries: { ...state.entries, [action.entry.date]: action.entry },
      };
    case 'SET_ENTRIES':
      return { ...state, entries: action.entries };
    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };
    case 'RESET_YEAR_DATA':
      return { ...state, entries: {}, carryOverVacation: null };
    case 'IMPORT_DATA': {
      // Feiertage direkt beim Import hinzufügen, um Race Conditions zu vermeiden
      const holidays = getHolidaysForState(action.year, action.settings.federalState);
      const entriesWithHolidays = { ...action.entries };

      holidays.forEach((holiday) => {
        const existingEntry = entriesWithHolidays[holiday.date];
        if (!existingEntry) {
          entriesWithHolidays[holiday.date] = {
            date: holiday.date,
            hours: 0,
            isHoliday: true,
            isVacation: false,
            note: holiday.name,
          };
        } else if (!existingEntry.isHoliday) {
          entriesWithHolidays[holiday.date] = {
            ...existingEntry,
            isHoliday: true,
            note: existingEntry.note || holiday.name,
          };
        }
      });

      return {
        ...state,
        entries: entriesWithHolidays,
        settings: action.settings,
      };
    }
    case 'SET_CARRY_OVER':
      return { ...state, carryOverVacation: action.carryOver };
    case 'SET_SETTINGS_SEGMENTS':
      return { ...state, settingsSegments: action.settingsSegments };
    case 'SET_USER_WAGE_TYPE':
      return { ...state, userWageType: action.wageType };
    default:
      return state;
  }
}

function loadSettings(): CompanySettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Fehler beim Laden der Einstellungen:', e);
  }
  return DEFAULT_SETTINGS;
}

function loadEntries(year: number): Record<string, DayEntry> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ENTRIES_PREFIX + year);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Fehler beim Laden der Einträge:', e);
  }
  return {};
}

function loadCarryOver(year: number): VacationCarryOver | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CARRY_OVER_PREFIX + year);
    if (stored) {
      const carryOver = JSON.parse(stored) as VacationCarryOver;
      // Prüfe ob der Übertrag verfallen ist (nach 31. März)
      const today = new Date();
      const expirationDate = new Date(carryOver.expirationDate);
      if (today > expirationDate) {
        carryOver.expired = true;
      }
      return carryOver;
    }
  } catch (e) {
    console.error('Fehler beim Laden des Urlaubsübertrags:', e);
  }
  return null;
}

function loadSettingsSegments(year: number): SettingsSegment[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS_SEGMENTS_PREFIX + year);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Fehler beim Laden der Settings-Segmente:', e);
  }
  return [];
}

function loadLastYear(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_YEAR);
    if (stored) {
      const year = parseInt(stored, 10);
      if (year >= 2020 && year <= 2030) {
        return year;
      }
    }
  } catch (e) {
    console.error('Fehler beim Laden des Jahres:', e);
  }
  return new Date().getFullYear();
}

function saveSettings(settings: CompanySettings): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (e) {
    console.error('Fehler beim Speichern der Einstellungen:', e);
  }
}

function saveEntries(year: number, entries: Record<string, DayEntry>): void {
  try {
    localStorage.setItem(STORAGE_KEYS.ENTRIES_PREFIX + year, JSON.stringify(entries));
  } catch (e) {
    console.error('Fehler beim Speichern der Einträge:', e);
  }
}

function saveCarryOver(year: number, carryOver: VacationCarryOver | null): void {
  try {
    if (carryOver) {
      localStorage.setItem(STORAGE_KEYS.CARRY_OVER_PREFIX + year, JSON.stringify(carryOver));
    } else {
      localStorage.removeItem(STORAGE_KEYS.CARRY_OVER_PREFIX + year);
    }
  } catch (e) {
    console.error('Fehler beim Speichern des Urlaubsübertrags:', e);
  }
}

function saveSettingsSegments(year: number, segments: SettingsSegment[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS_SEGMENTS_PREFIX + year, JSON.stringify(segments));
  } catch (e) {
    console.error('Fehler beim Speichern der Settings-Segmente:', e);
  }
}

function saveLastYear(year: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_YEAR, year.toString());
  } catch (e) {
    console.error('Fehler beim Speichern des Jahres:', e);
  }
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const initialYear = loadLastYear();
  const initialSettings = loadSettings();
  const initialEntries = loadEntries(initialYear);
  const initialCarryOver = loadCarryOver(initialYear);
  const initialSettingsSegments = loadSettingsSegments(initialYear);

  const [state, dispatch] = useReducer(appReducer, {
    year: initialYear,
    settings: initialSettings,
    entries: initialEntries,
    carryOverVacation: initialCarryOver,
    settingsSegments: initialSettingsSegments,
    isLoading: false,
  });

  // Speichere Änderungen
  useEffect(() => {
    saveSettings(state.settings);
  }, [state.settings]);

  useEffect(() => {
    saveEntries(state.year, state.entries);
  }, [state.year, state.entries]);

  useEffect(() => {
    saveCarryOver(state.year, state.carryOverVacation);
  }, [state.year, state.carryOverVacation]);

  useEffect(() => {
    saveSettingsSegments(state.year, state.settingsSegments);
  }, [state.year, state.settingsSegments]);

  useEffect(() => {
    saveLastYear(state.year);
  }, [state.year]);

  const setYear = useCallback((year: number) => {
    dispatch({ type: 'SET_LOADING', isLoading: true });
    const entries = loadEntries(year);
    const carryOver = loadCarryOver(year);
    const segments = loadSettingsSegments(year);
    dispatch({ type: 'SET_ENTRIES', entries });
    dispatch({ type: 'SET_CARRY_OVER', carryOver });
    dispatch({ type: 'SET_SETTINGS_SEGMENTS', settingsSegments: segments });
    dispatch({ type: 'SET_YEAR', year });
    dispatch({ type: 'SET_LOADING', isLoading: false });
  }, []);

  const updateSettings = useCallback((settings: CompanySettings) => {
    dispatch({ type: 'SET_SETTINGS', settings });
  }, []);

  const setSettingsSegments = useCallback((segments: SettingsSegment[]) => {
    dispatch({ type: 'SET_SETTINGS_SEGMENTS', settingsSegments: segments });
  }, []);

  const setUserWageType = useCallback((wageType: WageType | null) => {
    dispatch({ type: 'SET_USER_WAGE_TYPE', wageType });
  }, []);

  const updateEntry = useCallback((entry: DayEntry) => {
    dispatch({ type: 'UPDATE_ENTRY', entry });
  }, []);

  const resetYearData = useCallback(() => {
    dispatch({ type: 'RESET_YEAR_DATA' });
  }, []);

  const importData = useCallback((entries: Record<string, DayEntry>, settings: CompanySettings, year?: number) => {
    dispatch({ type: 'IMPORT_DATA', entries, settings, year: year ?? state.year });
  }, [state.year]);

  // Ref um zu verhindern, dass Feiertage mehrfach initialisiert werden
  const holidaysInitializedRef = useRef<string | null>(null);

  const initializeHolidays = useCallback(() => {
    const key = `${state.year}-${state.settings.federalState}`;

    // Verhindere mehrfache Initialisierung für dasselbe Jahr/Bundesland
    if (holidaysInitializedRef.current === key) {
      return;
    }
    holidaysInitializedRef.current = key;

    const holidays = getHolidaysForState(state.year, state.settings.federalState);
    const newEntries = { ...state.entries };
    let hasChanges = false;

    holidays.forEach((holiday) => {
      const existingEntry = newEntries[holiday.date];
      if (!existingEntry) {
        newEntries[holiday.date] = {
          date: holiday.date,
          hours: 0,
          isHoliday: true,
          isVacation: false,
          note: holiday.name,
        };
        hasChanges = true;
      } else if (!existingEntry.isHoliday) {
        newEntries[holiday.date] = {
          ...existingEntry,
          isHoliday: true,
          note: existingEntry.note || holiday.name,
          // Manager-Anmerkung beibehalten
          managerNote: existingEntry.managerNote,
          managerNoteAuthorId: existingEntry.managerNoteAuthorId,
          managerNoteAuthorName: existingEntry.managerNoteAuthorName,
          managerNoteTimestamp: existingEntry.managerNoteTimestamp,
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      dispatch({ type: 'SET_ENTRIES', entries: newEntries });
    }
  }, [state.year, state.settings.federalState, state.entries]);

  // Betriebsferien dynamisch anwenden (basierend auf CompanyVacationRule)
  // WageType-Differenzierung: FIXED = erzwungener Urlaub, HOURLY/null = gesperrter Tag ohne Urlaubsabzug.
  const applyCompanyVacation = useCallback((dates: Set<string>, wageType: WageType | null) => {
    const newEntries = { ...state.entries };
    let hasChanges = false;

    // Alte Betriebsferien entfernen (falls Regel geändert wurde).
    // Nur die maschinell gesetzte Default-Notiz löschen — manuelle Notizen,
    // die der User selbst angelegt hat, bleiben erhalten.
    for (const [date, entry] of Object.entries(newEntries)) {
      if (entry.isCompanyVacation && !dates.has(date)) {
        const updated: DayEntry = {
          ...entry,
          isCompanyVacation: false,
          isVacation: false,
        };
        if (entry.note === COMPANY_VACATION_DEFAULT_NOTE) {
          updated.note = undefined;
        }
        newEntries[date] = updated;
        hasChanges = true;
      }
    }

    // Neue Betriebsferien setzen (nur für Tage die noch nicht als Betriebsferien markiert sind)
    const newDates = new Set<string>();
    dates.forEach((date) => {
      if (!newEntries[date]?.isCompanyVacation) {
        newDates.add(date);
        hasChanges = true;
      }
    });

    const finalEntries = newDates.size > 0
      ? mergeCompanyVacationIntoEntries(newEntries, newDates, wageType)
      : newEntries;

    if (hasChanges) {
      dispatch({ type: 'SET_ENTRIES', entries: finalEntries });
    }
  }, [state.entries]);

  // Resturlaub ins nächste Jahr übertragen
  const carryOverVacationFn = useCallback(
    (days: number) => {
      if (days <= 0) return;

      const nextYear = state.year + 1;
      // Verfallsdatum: 31. März des Folgejahres
      const expirationDate = `${nextYear}-03-31`;

      const carryOver: VacationCarryOver = {
        fromYear: state.year,
        days,
        expirationDate,
        expired: false,
      };

      // Speichere den Übertrag für das nächste Jahr
      saveCarryOver(nextYear, carryOver);

      // Wenn wir uns im nächsten Jahr befinden, aktualisiere auch den State
      if (state.year === nextYear) {
        dispatch({ type: 'SET_CARRY_OVER', carryOver });
      }
    },
    [state.year]
  );

  // Übertrag löschen
  const clearCarryOver = useCallback(() => {
    dispatch({ type: 'SET_CARRY_OVER', carryOver: null });
  }, []);

  // Übertrag direkt setzen (z.B. nach API-Load)
  const setCarryOver = useCallback((carryOver: VacationCarryOver | null) => {
    dispatch({ type: 'SET_CARRY_OVER', carryOver });
  }, []);

  // Berechne verfügbaren Übertrag aus dem Vorjahr (nicht verfallen)
  const getCarryOverFromPreviousYear = useCallback((): number => {
    if (!state.carryOverVacation) return 0;
    if (state.carryOverVacation.expired) return 0;
    if (state.carryOverVacation.fromYear !== state.year - 1) return 0;
    return state.carryOverVacation.days;
  }, [state.carryOverVacation, state.year]);

  // Lade Einträge aus dem Vorjahr (für 13-Wochen-Berechnung)
  const getPreviousYearEntries = useCallback((): Record<string, DayEntry> => {
    return loadEntries(state.year - 1);
  }, [state.year]);

  return (
    <AppContext.Provider
      value={{
        state,
        setYear,
        updateSettings,
        updateEntry,
        resetYearData,
        importData,
        initializeHolidays,
        applyCompanyVacation,
        carryOverVacation: carryOverVacationFn,
        clearCarryOver,
        setCarryOver,
        getCarryOverFromPreviousYear,
        getPreviousYearEntries,
        setSettingsSegments,
        setUserWageType,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
